import React, {useState} from 'react';
import {Button, Form, Modal, Spinner } from "react-bootstrap";
import {ModalType} from "../../../common/common-models";
import { CHARGING_FEE_CALCULATION_TYPE_OPTIONS, ChargingFeeCalculationType, ChargingReportConfiguration, ChargingReportConfigurationForm } from '../models';
import { EditChargingReportConfigurationConfirmModal } from './EditChargingReportConfigurationConfirmModal';
import { ChargingReportConfigurationsService } from '../ChargingReportConfigurations.service';
import Select from "react-select";
import { onNumberChange } from 'helpers';
import { isNullOrUndefined } from 'common/data-util';
import { isEmailValidAndPresent } from 'helpers/validator';

interface EditChargingReportConfigurationModalProps {
    chargingReportConfiguration?: ChargingReportConfiguration
    modalType: ModalType;
    handleModalHide: () => void;
    loadChargingReportConfigurations: () => void;
}

export function EditChargingReportConfigurationModal(props: EditChargingReportConfigurationModalProps) {
    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);
    const [validated, setValidated]: [boolean, any] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible]: [boolean, any] = useState(false);
    const [form, setForm]: [ChargingReportConfigurationForm, any] = useState({
        externalChargingStationId: props.chargingReportConfiguration?.externalChargingStationId,
        chargingStationName: props.chargingReportConfiguration?.chargingStationName,
        scheduledDay: props.chargingReportConfiguration?.scheduledDay,
        feeCalculationType: props.chargingReportConfiguration?.feeCalculationType,
        monthlyFixedFeeEuros: props.chargingReportConfiguration?.monthlyFixedFeeEuros,
        chargingServiceFeeMarginalEurosKwh: props.chargingReportConfiguration?.chargingServiceFeeMarginalEurosKwh,
        chargingServiceFeePercentage: props.chargingReportConfiguration?.chargingServiceFeePercentage,
        includeInvoiceInfo: props.chargingReportConfiguration?.includeInvoiceInfo ?? false,
        includeSessionAmount: props.chargingReportConfiguration?.includeSessionAmount ?? false,
        includeProductDescription: props.chargingReportConfiguration?.includeProductDescription ?? false,
        includeTimeTariffs: props.chargingReportConfiguration?.includeTimeTariffs ?? false,
        includeCustomerName: props.chargingReportConfiguration?.includeCustomerName ?? false,
        recipientEmails: props.chargingReportConfiguration?.recipientEmails,
    });

    const validateForm = () => {
        if (!form.externalChargingStationId?.trim()) return false;
        if (!isValidScheduledDay(form.scheduledDay)) return false;
        if (!form.feeCalculationType) return false;
    
        if (form.feeCalculationType === ChargingFeeCalculationType.MONTHLY_FIXED_FEE && isNullOrUndefined(form.monthlyFixedFeeEuros)) {
            return false;
        }
    
        if (form.feeCalculationType === ChargingFeeCalculationType.MARGIN && isNullOrUndefined(form.chargingServiceFeeMarginalEurosKwh)) {
            return false;
        }
    
        if (form.feeCalculationType === ChargingFeeCalculationType.PERCENTAGE_OF_TURNOVER && isNullOrUndefined(form.chargingServiceFeePercentage)) {
            return false;
        }
    
        if (!form.recipientEmails || !isValidEmail(form.recipientEmails)) return false;
    
        return true;
    };

    const isValidEmail = (emails?: string[]) => {
        if(!emails || !emails.length) return false;
        return emails.every(email => isEmailValidAndPresent(email));
    }

    const isValidScheduledDay = (val?: any) => {
        if(!val) return false;
        if(Number(val) >= 1 && Number(val) <= 28) return true;
        return false;
    }

    const validateSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        setValidated(true);
        if (isValidForm) {
            setIsConfirmModalVisible(true);
        } else {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        setSavingError(false);

        try{
            if(props.modalType === ModalType.CREATE){
                await ChargingReportConfigurationsService.postChargingReportConfiguration(form);
            }else{
                await ChargingReportConfigurationsService.putChargingReportConfiguration(props.chargingReportConfiguration!.id, form);
            }

            setSaving(false);
            setTimeout(() => {
                props.handleModalHide();
                props.loadChargingReportConfigurations();
            }, 300);
        }catch(e){
            console.error('Saving failed', e);
            setSaving(false);
            setSavingError(true);
        }
    };

    const invoiceInfoTypes = [ChargingFeeCalculationType.PERCENTAGE_OF_TURNOVER, ChargingFeeCalculationType.MARGIN];
    const noInvoiceInfoTypes = [ChargingFeeCalculationType.NONE, ChargingFeeCalculationType.MONTHLY_FIXED_FEE];

    const allowedTypes = form.includeInvoiceInfo
        ? invoiceInfoTypes
        : noInvoiceInfoTypes;
  
    const calculationTypeSelectOptions = CHARGING_FEE_CALCULATION_TYPE_OPTIONS.filter(opt => allowedTypes.includes(opt.value));

    const calculationTypeSelectCurrentOption = calculationTypeSelectOptions
        .find(opt => opt.value === form.feeCalculationType);

    return (
        <Modal
            show={true}
            size={"lg"}
            onHide={props.handleModalHide}
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    {
                        props.modalType === ModalType.CREATE &&
                        <span>Laadimisraporti seadistuse lisamine</span>
                    }
                    {
                        props.modalType === ModalType.EDIT &&
                        <span>Laadimisraporti seadistuse muutmine</span>
                    }
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form 
                    noValidate 
                    validated={validated} onSubmit={(e: any) => e.preventDefault()}
                    id={"electricity-package-form"}
                    className={'mt-1 align-self-center m-0'}
                >
                    <Form.Group controlId={'form-external-charging-station-id'} className="row m-0">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Laadimisjaama NOOM ID*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                minLength={1}
                                value={form.externalChargingStationId}
                                onChange={(e) => setForm({
                                    ...form,
                                    externalChargingStationId: e.target.value
                                })} 
                                disabled={saving}
                                type="text"
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-charging-station-name'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Laadimisjaama nimi</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                value={form.chargingStationName}
                                onChange={(e) => setForm({
                                    ...form,
                                    chargingStationName: e.target.value
                                })} 
                                disabled={saving}
                                type="text"
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-scheduled-day'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Raporti koostamise päev*</span>
                        </Form.Label>
                        <div className="p-0">
                            <Form.Control 
                                required
                                value={form.scheduledDay}
                                onChange={(e) => setForm({
                                    ...form,
                                    scheduledDay: !e.target.value || isValidScheduledDay(e.target.value) ? e.target.value: form.scheduledDay
                                })}
                                disabled={saving}
                                type="number"
                                max={28}
                                min={1}
                                onWheel={(e: any) => e.target.blur()}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-include-invoice-info'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Arve koostamise info arvel</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-include-invoice-info'
                                checked={form.includeInvoiceInfo}
                                onChange={(e: any) => {
                                    let feeCalculationType = form.feeCalculationType;
                                    if(e.target.checked && feeCalculationType && !invoiceInfoTypes.includes(feeCalculationType)){
                                        feeCalculationType = undefined;
                                    }
                                    if(!e.target.checked && feeCalculationType && !noInvoiceInfoTypes.includes(feeCalculationType)){
                                        feeCalculationType = undefined;
                                    }

                                    setForm({
                                        ...form,
                                        includeInvoiceInfo: e.target.checked,
                                        feeCalculationType,
                                        monthlyFixedFeeEuros: undefined,
                                        chargingServiceFeeMarginalEurosKwh: undefined,
                                        chargingServiceFeePercentage: undefined,
                                    })
                                }}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-fee-calculation-type'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Tasu arvestamise viis*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Select
                                required
                                options={calculationTypeSelectOptions}
                                value={calculationTypeSelectCurrentOption ?? null}
                                onChange={(opt: any) => {
                                    const includeSessionAmount = opt.value === ChargingFeeCalculationType.PERCENTAGE_OF_TURNOVER || opt.value === ChargingFeeCalculationType.MARGIN
                                     ? true : form.includeSessionAmount

                                    setForm({
                                        ...form,
                                        feeCalculationType: opt.value,
                                        monthlyFixedFeeEuros: undefined,
                                        chargingServiceFeeMarginalEurosKwh: undefined,
                                        chargingServiceFeePercentage: undefined,
                                        includeSessionAmount
                                    });
                                }}
                                placeholder='Vali tasu arvestamise viis'
                                className={'w-100 form-control form-select' + (!form.feeCalculationType && validated ? ' is-invalid' : '')}
                            />
                            <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    {
                        form.feeCalculationType ===  ChargingFeeCalculationType.MONTHLY_FIXED_FEE ? (
                            <Form.Group controlId={'form-monthly-fixed-fee-euros'} className="row m-0 mt-3">
                                <Form.Label className={'col-5'}>
                                    <span className="text-wrap">Igakuine raporti väljastamise tasu (€)*</span>
                                </Form.Label>
                                <div className="col p-0">
                                    <Form.Control
                                        required
                                        value={form.monthlyFixedFeeEuros ?? ''}
                                        onChange={(e) => setForm({
                                            ...form,
                                            monthlyFixedFeeEuros: onNumberChange(e)
                                        })}
                                        type="number"
                                        onWheel={(e: any) => e.target.blur()}
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                </div>
                            </Form.Group>
                        ) : null
                    }

                    {
                        form.feeCalculationType ===  ChargingFeeCalculationType.MARGIN ? (
                            <Form.Group controlId={'form-charging-service-fee-marginal'} className="row m-0 mt-3">
                                <Form.Label className={'col-5'}>
                                    <span className="text-wrap">Teenustasu marginaal (€/kWh)*</span>
                                </Form.Label>
                                <div className="col p-0">
                                    <Form.Control
                                        required
                                        value={form.chargingServiceFeeMarginalEurosKwh ?? ''}
                                        onChange={(e) => setForm({
                                            ...form,
                                            chargingServiceFeeMarginalEurosKwh: onNumberChange(e)
                                        })}
                                        type="number"
                                        onWheel={(e: any) => e.target.blur()}
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                </div>
                            </Form.Group>
                        ) : null
                    }

                    {
                        form.feeCalculationType ===  ChargingFeeCalculationType.PERCENTAGE_OF_TURNOVER ? (
                            <Form.Group controlId={'form-charging-service-fee-percentage'} className="row m-0 mt-3">
                                <Form.Label className={'col-5'}>
                                    <span className="text-wrap">Teenustasu protsent (%)*</span>
                                </Form.Label>
                                <div className="col p-0">
                                    <Form.Control
                                        required
                                        value={form.chargingServiceFeePercentage ?? ''}
                                        onChange={(e) => setForm({
                                            ...form,
                                            chargingServiceFeePercentage: onNumberChange(e)
                                        })}
                                        type="number"
                                        onWheel={(e: any) => e.target.blur()}
                                    />
                                    <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                </div>
                            </Form.Group>
                        ) : null
                    }

                    <Form.Group controlId={'form-recipient-emails'} className="row m-0 mt-3">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Meiliaadressid*</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Form.Control
                                required
                                minLength={1}
                                value={form.recipientEmails?.join(",")}
                                onChange={(e) => setForm({
                                    ...form,
                                    recipientEmails: e.target.value.split(",")
                                })
                                } 
                                disabled={saving}
                                as="textarea"
                                rows={3}
                                isInvalid={validated && (!form.recipientEmails || !isValidEmail(form.recipientEmails))}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </div>
                    </Form.Group>

                    <h2 className='col mt-3'>Kuvamine</h2>

                    <Form.Group controlId={'form-include-session-amount'} className="row m-0 mt-1">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Laadimissessiooni summa eurodes</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-include-session-amount'
                                checked={form.includeSessionAmount}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    includeSessionAmount: e.target.checked
                                })}
                                disabled={saving || form.feeCalculationType === ChargingFeeCalculationType.PERCENTAGE_OF_TURNOVER || form.feeCalculationType === ChargingFeeCalculationType.MARGIN}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-include-product-description'} className="row m-0 mt-1">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Laetud toote kirjeldus</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-include-product-description'
                                checked={form.includeProductDescription}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    includeProductDescription: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-include-time-tariffs'} className="row m-0 mt-1">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Ajatariifide arvestus</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-include-time-tariffs'
                                checked={form.includeTimeTariffs}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    includeTimeTariffs: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>

                    <Form.Group controlId={'form-include-customer-name'} className="row m-0 mt-1">
                        <Form.Label className={'col-5'}>
                            <span className="text-wrap">Kliendi nimi</span>
                        </Form.Label>
                        <div className="col-3 p-0">
                            <Form.Check
                                className='w-full form-check-input-no-label'
                                id='form-include-customer-name'
                                checked={form.includeCustomerName}
                                onChange={(e: any) => setForm({
                                    ...form,
                                    includeCustomerName: e.target.checked
                                })}
                                disabled={saving}
                                type='checkbox'
                            />
                        </div>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                <Button variant="info"
                        onClick={() => props.handleModalHide()}
                        type="button"
                        className={"d-flex align-items-center"}>
                    <span className='px-2'>Tühista</span>
                </Button>
                <Button variant="primary" onClick={validateSubmit} type="button" disabled={saving}
                        className="d-flex align-items-center">
                    {
                        !saving &&
                        <span className='px-2'>Salvesta</span>
                    }
                    {
                        saving &&
                        <>
                            <span className='px-2'>Salvestab…</span>
                            <Spinner size={"sm"} animation={"border"}/>
                        </>
                    }
                </Button>

            </Modal.Footer>
            {
                !saving && savingError &&
                <span className="d-flex justify-content-end text-danger px-2 pb-2">
                    Viga salvestamisel, proovi mõne hetke pärast uuesti.
                </span>
            }
            {
                validated && isConfirmModalVisible &&
                    <EditChargingReportConfigurationConfirmModal
                        handleConfirmSubmit={() => {
                            setIsConfirmModalVisible(false);
                            handleSubmit()
                        }}
                        handleConfirmReject={() => {
                            setSaving(false);
                            setIsConfirmModalVisible(false);
                        }}
                        modalType={props.modalType}/>
            }
        </Modal>
    );
}
